import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import icon from 'flarum/common/helpers/icon';

/**
 * Whether any of this user's stories is followers-only.
 *
 * Those get a green ring, so the audience is visible before opening.
 */
export function hasFollowersOnly(group) {
  return (group.stories || []).some((story) => story.visibility === 'followers');
}

/**
 * Whether any of this user's stories carries a poll the actor has not answered.
 *
 * Shown as a badge on the circle: people were opening stories without knowing a
 * poll was inside, and a poll nobody notices collects no answers. Once answered
 * the badge goes, so the circle stops asking for something already given.
 */
export function hasUnansweredPoll(group) {
  return (group.stories || []).some(
    (story) =>
      story.poll && (story.poll.myAnswer === null || story.poll.myAnswer === undefined)
  );
}

/** Gap between ring segments, in degrees. */
const SEGMENT_GAP = 6;

/**
 * A single user's story circle.
 *
 * One circle represents one user, however many stories they posted. When there
 * are several, the ring is split into segments — like Instagram — so the count
 * is visible before opening the viewer.
 *
 * Props:
 *   group   – {user, isViewed, stories[]} from API
 *   onclick – callback when circle is tapped
 */
export default class StoryCircle extends Component {
  view() {
    const { group, onclick } = this.attrs;
    const user   = group.user;
    const viewed = group.isViewed;
    // Model.id() yields a string while the API sends a number, so these are
    // compared numerically — a strict match never held and the actor's own
    // circle was labelled with their name instead of "your story".
    const isOwn  = app.session.user && Number(app.session.user.id()) === Number(user.id);

    return (
      <div
        className={'StoryCircle' + (viewed ? ' StoryCircle--viewed' : '') + (isOwn ? ' StoryCircle--own' : '')}
        onclick={onclick}
        title={user.displayName}
      >
        {/* Gradient ring — segmented when the user has several stories */}
        <div
          className={
            'StoryCircle-ring' +
            (viewed ? ' StoryCircle-ring--viewed' : '') +
            (hasFollowersOnly(group) ? ' StoryCircle-ring--followers' : '')
          }
          style={this.ringStyle(group)}
        >
          <div className="StoryCircle-inner">
            {user.avatarUrl
              ? <img className="StoryCircle-avatar" src={user.avatarUrl} alt={user.displayName} />
              : <div className="StoryCircle-avatarFallback">{(user.displayName || user.username || '?')[0].toUpperCase()}</div>
            }
          </div>

          {hasUnansweredPoll(group) ? (
            <span
              className="StoryCircle-pollBadge"
              title={app.translator.trans('happydigital-stories.forum.poll_waiting')}
            >
              {icon('fas fa-poll')}
            </span>
          ) : null}
        </div>

        {/* Username label */}
        <span className="StoryCircle-name">
          {isOwn ? app.translator.trans('happydigital-stories.forum.your_story') : (user.displayName || user.username)}
        </span>
      </div>
    );
  }

  /**
   * Build the segmented ring as a conic-gradient overriding the CSS one.
   *
   * A single story keeps the stylesheet's own ring, so the common case looks
   * exactly as before and needs no inline style at all.
   */
  ringStyle(group) {
    const count = group.stories.length;

    if (count < 2) return {};

    const seen   = group.stories.filter((s) => s.isViewed).length;
    const slice  = 360 / count;
    const stops  = [];

    for (let i = 0; i < count; i++) {
      const start = i * slice;
      const end   = start + slice - SEGMENT_GAP;
      // Seen stories fade out, matching how the whole ring greys once viewed.
      const lit = hasFollowersOnly(group) ? '#2e7d32' : '#e1306c';
      const color = i < seen ? 'var(--muted-color, #c7c7c7)' : lit;

      stops.push(`${color} ${start}deg ${end}deg`);
      stops.push(`transparent ${end}deg ${start + slice}deg`);
    }

    return { background: `conic-gradient(${stops.join(', ')})` };
  }
}
